import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getIntlAppointments,
  sendCommitteeMessage,
} from '../../../endpoints/chapters';
import { StandingAppointmentsResponse } from '../../../endpoints/chaptersTs';
import PositionDisplay from '../../../Components/PositionDisplay';
import AddPosition from '../../../Components/AddPosition';
import { getGroupRolesByType } from '../../../endpoints/groups';
import { LoadingButton } from '@mui/lab';
import { InternationalCommitteeMessage } from '@/types/chapters';
import {
  INTL_GROUP_TYPE_STANDING_APPOINTMENTS,
  IntlSaveData,
  saveIntlLeadershipPosition,
} from './intlLeadershipEdit';
import AlertItem from '../../../Components/AlertItem';

const standingApptGroup = 'ffd9f45f6f8f400180bcf25b7ab1afee';

const IntlAppointments: React.FC = () => {
  const queryClient = useQueryClient();
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [errorSaving, setErrorSaving] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data: appointmentsResponse, isFetching } =
    useApiGet<StandingAppointmentsResponse>(
      ['intl-appointments'],
      getIntlAppointments,
      {
        enabled: true,
        retry: 1,
      },
      {}
    );

  const canEdit = Boolean(appointmentsResponse?.can_edit);
  const appointmentsData = appointmentsResponse?.data || [];

  const { data: appointmentRoles } = useApiGet(
    ['intl-appointment-roles'],
    () => getGroupRolesByType(INTL_GROUP_TYPE_STANDING_APPOINTMENTS),
    { enabled: canEdit, retry: 1 },
    {}
  );

  const { mutate: sendMessage, isPending: isSendingMessage } = useApiSend(
    sendCommitteeMessage,
    () => {
      setMessageDialogOpen(false);
      setMessageText('');
    },
    (error) => {
      setErrorSaving(error?.message || 'Error sending message');
    }
  );

  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setMessageText('');
  };

  const onSavePosition = (position: IntlSaveData) => {
    setIsSaving(true);
    setErrorSaving(null);
    void saveIntlLeadershipPosition(position)
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['intl-appointments'] });
      })
      .catch((err: { message?: string }) => {
        setErrorSaving(err?.message || 'Failed to save position.');
      })
      .finally(() => setIsSaving(false));
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!appointmentsResponse) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  const handleMessageClick = () => {
    setMessageDialogOpen(true);
  };

  const handleSendMessage = () => {
    const messageData: InternationalCommitteeMessage = {
      committee_id: standingApptGroup,
      user_id: null,
      message: messageText.trim(),
    };
    sendMessage(messageData);
  };

  return (
    <Grid container>
      {errorSaving && (
        <Grid item xs={12} sx={{ px: 2, pt: 2 }}>
          <AlertItem
            severity="error"
            description={errorSaving}
            onClose={() => setErrorSaving(null)}
          />
        </Grid>
      )}
      {appointmentsData.map((appointment) => {
        const isStandingApptGroup = appointment.id === standingApptGroup;
        return (
          <Grid item xs={12} md={6} sx={{ padding: 2 }} key={appointment.id}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader
                title={
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6">{appointment.name}</Typography>
                    {isStandingApptGroup && (
                      <Button
                        onClick={handleMessageClick}
                        variant="contained"
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          pb: 0,
                          pt: 0,
                          pl: 1,
                          pr: 1,
                          ml: 1,
                        }}
                      >
                        Message
                      </Button>
                    )}
                  </Box>
                }
                subheader={appointment.description}
                sx={{ p: 1 }}
              />
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={2}>
                  {(appointment.members || [])
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .map((user) => (
                      <PositionDisplay
                        key={user.id}
                        id={user.id}
                        position={user.position || ''}
                        positionId={user.position_id || ''}
                        committeeId={
                          user.committee_id || user.group_id || appointment.id
                        }
                        name={user.name}
                        startDate={user.start_date}
                        endDate={user.end_date}
                        canEdit={canEdit}
                        isSaving={isSaving}
                        errorSavingText={errorSaving}
                        onSave={canEdit ? onSavePosition : null}
                        userId={user.user_id}
                        pageFrom={'intl-appointments'}
                        canMessage={false}
                      />
                    ))}
                  {canEdit && (
                    <Grid item xs={12} sx={{ p: 1 }}>
                      <AddPosition
                        buttonText="Add Position"
                        committeeId={appointment.id}
                        positions={(appointmentRoles || []).map((r: any) => ({
                          id: r.id,
                          name: r.name,
                          order: r.order,
                        }))}
                        onAdd={onSavePosition}
                        isSaving={isSaving}
                        savingError={errorSaving}
                        saveSuccess={null}
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        );
      })}

      <Dialog
        open={messageDialogOpen}
        onClose={handleCloseMessageDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Message to Standing Appointments</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Enter your message..."
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMessageDialog}>Cancel</Button>
          <LoadingButton
            onClick={handleSendMessage}
            variant="contained"
            loading={isSendingMessage}
            disabled={!messageText.trim()}
          >
            Send
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default IntlAppointments;
